import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import {
	OrderStatus,
	OrderStatusColors,
	OrderStatusLabels,
} from '../../../utils/orderStatus';
import type { ListBuilderColumn } from '../../types';

/**
 * Shared column definitions for order-client-embedded lists
 * Note: These differ from the main order columns:
 * - Status uses value-mapping instead of conditional (no alertStatus check)
 * - No client-info column (embedded in client context)
 * - Some style size differences (xs vs sm)
 *
 * Uses centralized colors from orderStatus.ts for consistency across the application.
 */

/**
 * Status and Origin column with status badge and origin badge (no alert icon)
 */
export const createStatusOriginColumn = (): ListBuilderColumn => ({
	id: 'status-origin',
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'status',
					objectTable: 'order',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'value-mapping',
					valueMapping: {
						[OrderStatus.UNSUBMITTED]:
							OrderStatusColors[OrderStatus.UNSUBMITTED],
						[OrderStatus.PENDING]: OrderStatusColors[OrderStatus.PENDING],
						[OrderStatus.SENT_TO_ERP]:
							OrderStatusColors[OrderStatus.SENT_TO_ERP],
						[OrderStatus.APPROVED]: OrderStatusColors[OrderStatus.APPROVED],
						[OrderStatus.SHIPPED]: OrderStatusColors[OrderStatus.SHIPPED],
						[OrderStatus.HELD]: OrderStatusColors[OrderStatus.HELD],
						[OrderStatus.CANCELLED]: OrderStatusColors[OrderStatus.CANCELLED],
						[OrderStatus.APPROVAL_REQUIRED]:
							OrderStatusColors[OrderStatus.APPROVAL_REQUIRED],
						[OrderStatus.OVERRIDE_REJECTED]:
							OrderStatusColors[OrderStatus.OVERRIDE_REJECTED],
						[OrderStatus.CANCELLATION_REQUESTED]:
							OrderStatusColors[OrderStatus.CANCELLATION_REQUESTED],
						[OrderStatus.SUBMITTED]: OrderStatusColors[OrderStatus.SUBMITTED],
						[OrderStatus.PARTIALLY_SHIPPED]:
							OrderStatusColors[OrderStatus.PARTIALLY_SHIPPED],
					},
				},
				valueMapping: [
					{
						color: OrderStatusColors[OrderStatus.UNSUBMITTED],
						value: OrderStatus.UNSUBMITTED,
						displayText: OrderStatusLabels[OrderStatus.UNSUBMITTED],
					},
					{
						color: OrderStatusColors[OrderStatus.PENDING],
						value: OrderStatus.PENDING,
						displayText: OrderStatusLabels[OrderStatus.PENDING],
					},
					{
						color: OrderStatusColors[OrderStatus.SENT_TO_ERP],
						value: OrderStatus.SENT_TO_ERP,
						displayText: OrderStatusLabels[OrderStatus.SENT_TO_ERP],
					},
					{
						color: OrderStatusColors[OrderStatus.APPROVED],
						value: OrderStatus.APPROVED,
						displayText: OrderStatusLabels[OrderStatus.APPROVED],
					},
					{
						color: OrderStatusColors[OrderStatus.SHIPPED],
						value: OrderStatus.SHIPPED,
						displayText: OrderStatusLabels[OrderStatus.SHIPPED],
					},
					{
						color: OrderStatusColors[OrderStatus.HELD],
						value: OrderStatus.HELD,
						displayText: OrderStatusLabels[OrderStatus.HELD],
					},
					{
						color: OrderStatusColors[OrderStatus.CANCELLED],
						value: OrderStatus.CANCELLED,
						displayText: OrderStatusLabels[OrderStatus.CANCELLED],
					},
					{
						color: OrderStatusColors[OrderStatus.APPROVAL_REQUIRED],
						value: OrderStatus.APPROVAL_REQUIRED,
						displayText: OrderStatusLabels[OrderStatus.APPROVAL_REQUIRED],
					},
					{
						color: OrderStatusColors[OrderStatus.OVERRIDE_REJECTED],
						value: OrderStatus.OVERRIDE_REJECTED,
						displayText: OrderStatusLabels[OrderStatus.OVERRIDE_REJECTED],
					},
					{
						color: OrderStatusColors[OrderStatus.CANCELLATION_REQUESTED],
						value: OrderStatus.CANCELLATION_REQUESTED,
						displayText: OrderStatusLabels[OrderStatus.CANCELLATION_REQUESTED],
					},
					{
						color: OrderStatusColors[OrderStatus.SUBMITTED],
						value: OrderStatus.SUBMITTED,
						displayText: OrderStatusLabels[OrderStatus.SUBMITTED],
					},
					{
						color: OrderStatusColors[OrderStatus.PARTIALLY_SHIPPED],
						value: OrderStatus.PARTIALLY_SHIPPED,
						displayText: OrderStatusLabels[OrderStatus.PARTIALLY_SHIPPED],
					},
				],
			},
			displayStyle: 'badge',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'origin-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'origin', objectTable: 'order' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'value-mapping',
					valueMapping: {
						'1': { bg: '#e5e7eb', text: '#374151' },
						'2': { bg: '#ddd6fe', text: '#6b21a8' },
						'3': { bg: '#dbeafe', text: '#1e40af' },
					},
				},
				valueMapping: [
					{
						color: { bg: '#e5e7eb', text: '#374151' },
						value: '1',
						displayText: 'CRM',
					},
					{
						color: { bg: '#ddd6fe', text: '#6b21a8' },
						value: '2',
						displayText: 'ERP',
					},
					{
						color: { bg: '#dbeafe', text: '#1e40af' },
						value: '3',
						displayText: 'E-Commerce',
					},
				],
			},
			displayStyle: 'badge',
		},
	],
	headers: [
		{ id: 'h1-status', text: 'Status' },
		{ id: 'h1-origin', text: 'Origin' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'status', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order and ERP numbers column
 */
export const createOrderErpNumbersColumn = (): ListBuilderColumn => ({
	id: 'order-erp-numbers',
	fields: [
		{
			id: 'order-id-field',
			link: {
				type: 'object',
				enabled: true,
				idField: '_id',
				objectTable: 'order',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: '_id', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'static', staticColor: { text: '#3b82f6' } },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-2',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'external-id-field',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'externalId2',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: { fieldName: 'externalId2', objectTable: 'order' },
						},
					},
				],
				defaultDataConfig: {
					type: 'field',
					field: { fieldName: 'externalId', objectTable: 'order' },
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h2-order', text: 'Order #' },
		{ id: 'h2-erp', text: 'ERP #' },
	],
	minWidth: 100,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'id', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

/**
 * Dates column with order date and last updated
 */
export const createDatesColumn = (): ListBuilderColumn => ({
	id: 'dates',
	fields: [
		{
			id: 'date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'date', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: { dateFormat: 'datetime-short' },
				allowTextWrap: false,
			},
			displayStyle: 'date',
		},
		{
			id: 'line-break-5',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'updated-date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: '_modified_date', objectTable: 'order' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { dateFormat: 'datetime-short' },
				allowTextWrap: false,
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{ id: 'h4-date', text: 'Date' },
		{ id: 'h4-updated', text: 'Last Updated' },
	],
	minWidth: 110,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'DESC', fieldName: 'date', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Reps column with assigned rep and submitted by
 */
export const createRepsColumn = (): ListBuilderColumn => ({
	id: 'reps',
	fields: [
		{
			id: 'assigned-rep-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'assignedRepName', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{
			id: 'line-break-6',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'submitted-by-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'submitterName', objectTable: 'order' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h5-assigned', text: 'Assigned Rep' },
		{ id: 'h5-submitted', text: 'Submitted By' },
	],
	minWidth: 140,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'ASC', fieldName: 'assignedRepName', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order lines column
 */
export const createOrderLinesColumn = (): ListBuilderColumn => ({
	id: 'order-lines',
	fields: [
		{
			id: 'lines-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'numberOfLines', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h6', text: 'Lines' }],
	minWidth: 60,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'DESC', fieldName: 'numberOfLines', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

/**
 * Order costs column with tax, freight, and discount
 */
export const createOrderCostsColumn = (): ListBuilderColumn => ({
	id: 'order-costs',
	fields: [
		{
			id: 'tax-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'tax', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
		},
		{
			id: 'line-break-7',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'freight-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'freight', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-8',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'discount-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'discount', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h7-tax', text: 'Tax' },
		{ id: 'h7-freight', text: 'Freight' },
		{ id: 'h7-discount', text: 'Discount' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [{ direction: 'DESC', fieldName: 'tax', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order totals column with subtotal and total
 */
export const createOrderTotalsColumn = (): ListBuilderColumn => ({
	id: 'order-totals',
	fields: [
		{
			id: 'subtotal-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'subtotal', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
		},
		{
			id: 'line-break-9',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'total-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'total', objectTable: 'order' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h8-subtotal', text: 'Subtotal' },
		{ id: 'h8-total', text: 'Total' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [{ direction: 'DESC', fieldName: 'total', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * GP totals column
 */
export const createGpTotalsColumn = (): ListBuilderColumn => ({
	id: 'gp-totals',
	fields: [
		{
			id: 'total-gp-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalGP', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
			// Field-level only: keeps GP out of CSV exports. FieldRenderer
			// renders a lock icon in-cell so the header stays visible.
			requiredPermission: GP_VIEW_PERMISSION,
		},
	],
	headers: [{ id: 'h-total-gp', text: 'GP' }],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [
		{ direction: 'DESC', fieldName: 'totalGP', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Standard column set for all order-client-embedded lists
 */
export const createStandardOrderClientEmbeddedColumns =
	(): Array<ListBuilderColumn> => [
		createStatusOriginColumn(),
		createOrderErpNumbersColumn(),
		createDatesColumn(),
		createRepsColumn(),
		createOrderLinesColumn(),
		createOrderCostsColumn(),
		createOrderTotalsColumn(),
		createGpTotalsColumn(),
	];
